import {
  useState,
  useEffect,
  useRef,
  forwardRef,
  useImperativeHandle,
  useMemo,
} from "react";
import toast from "react-hot-toast";

import { CallApiForGetFinalizedVisits } from "../../helpers/http";
import FormRenderer from "../formBuilder/FormRenderer";
import { getRateReviewTemplate } from "./rateReviewTemplate";

function getTokenData() {
  try {
    return JSON.parse(sessionStorage.getItem("tokenData") || "{}");
  } catch {
    return {};
  }
}
function getSelectedRow() {
  try {
    const direct = sessionStorage.getItem("selectedRow");
    if (direct) return JSON.parse(direct);
    const td = getTokenData();
    if (td.selectedRow) return JSON.parse(td.selectedRow);
    return {};
  } catch {
    return {};
  }
}
function getCommonServiceToken() {
  try {
    const raw = sessionStorage.getItem("commonServiceToken");
    if (!raw) return "";
    const parsed = JSON.parse(raw);
    return typeof parsed === "string" ? parsed : "";
  } catch {
    return "";
  }
}
function getEnv() {
  const h = window.location.hostname;
  return h === "localhost" || h.startsWith("dev") || h.startsWith("127.")
    ? "UAT"
    : "PROD";
}
function buildHeaders(token) {
  return {
    Accept: "application/json",
    "Content-Type": "application/json",
    Authorization: `Bearer ${token}`,
    ENV: getEnv(),
  };
}
function parseSavedFormData(formDataJson) {
  try {
    const parsed = JSON.parse(formDataJson);
    const map = {};
    (parsed.fields || []).forEach((f) => {
      if (f.value !== null && f.value !== undefined) map[f.id] = f.value;
    });
    return map;
  } catch {
    return {};
  }
}
function buildFormDataObj(schema, responses) {
  let fields = [];
  try {
    fields = JSON.parse(schema?.schemaJson || "{}").fields || [];
  } catch {}
  return {
    version: 1,
    fields: fields.map((f) => ({
      id: f.id,
      type: f.type,
      value: responses[f.id] ?? null,
    })),
  };
}

// function getTargetEventIds() {
//   const td = getTokenData();
//   const raw = td.eventIds ?? td.EventIds ?? td.EventId ?? td.eventId;
//   console.log("[RateUsStep] tokenData:", td, "raw eventIds:", raw);
//   if (Array.isArray(raw)) return raw.map(String).filter(Boolean);
//   if (typeof raw === "string" && raw.trim()) {
//     return raw
//       .split(",")
//       .map((s) => s.trim())
//       .filter(Boolean);
//   }
//   if (typeof raw === "number") return [String(raw)];
//   return [];
// }
function getTargetEventIds() {
  return getReviewEventIds();
}

async function apiFetchSchema(baseUrl, formTemplateId, companyId, token) {
  const url = `${baseUrl}api/CommonServiceAPI/v1/FormTemplate/GetFormTemplateById?formTemplateId=${formTemplateId}&companyId=${companyId}`;
  const res = await fetch(url, { method: "GET", headers: buildHeaders(token) });
  const result = await res.json();
  if (result.responseCode === 200) {
    const data = result.data || result.Data || null;
    if (data)
      return { ...data, schemaJson: data.schemaJson || data.schemaJsonPath };
  }
  return null;
}

// NOTE: `visitId` is passed in as the `caseId` query param, since the
// FormTemplate save/fetch endpoints key saved data off that identifier —
// now scoped per-visit rather than per-case.
async function apiFetchSaved(
  baseUrl,
  formTemplateId,
  visitId,
  companyId,
  token,
) {
  try {
    // const url = `${baseUrl}api/CommonServiceAPI/v1/FormTemplate/GetFormTemplateByCaseId?formTemplateId=${formTemplateId}&caseId=${visitId}&companyId=${companyId}`;
    const url = `${baseUrl}api/CommonServiceAPI/v1/FormTemplate/GetFeedbackFormTemplateByEventId?formTemplateId=${formTemplateId}&eventId=${visitId}&companyId=${companyId}`;
    // const url = `https://localhost:7053/api/CommonServiceAPI/v1/FormTemplate/GetFeedbackFormTemplateByEventId?formTemplateId=${formTemplateId}&eventId=${visitId}&companyId=${companyId}`;
    const res = await fetch(url, {
      method: "GET",
      headers: buildHeaders(token),
    });
    const result = await res.json();
    if (result.responseCode === 200 && result.data?.formData) {
      return parseSavedFormData(result.data.formData);
    }
  } catch (e) {
    console.error("[RateUsStep] apiFetchSaved error:", e);
  }
  return {};
}

async function apiSave(
  baseUrl,
  token,
  formTemplateId,
  visitId,
  companyId,
  userId,
  formName,
  formDataObj,
) {
  const res = await fetch(
    // `${baseUrl}api/CommonServiceAPI/v1/FormTemplate/SaveFeedbackFormTemplateData`,
    `${baseUrl}api/CommonServiceAPI/v1/FormTemplate/SaveFeedbackFormTemplateData`,
    // `https://localhost:7053/api/CommonServiceAPI/v1/FormTemplate/SaveFeedbackFormTemplateData`,
    {
      method: "POST",
      headers: buildHeaders(token),
      body: JSON.stringify({
        FormTemplateId: formTemplateId,
        CaseId: String(visitId),
        FormData: JSON.stringify(formDataObj),
        UserId: userId,
        CompanyId: companyId,
        FormName: formName,
        EventId: String(visitId),
      }),
    },
  );
  const result = await res.json();
  return result?.responseCode === 200;
}

function formatVisitDate(dateStr) {
  if (!dateStr) return "Visit";
  const d = new Date(dateStr);
  if (isNaN(d.getTime())) return "Visit";
  return d.toLocaleDateString(undefined, {
    year: "numeric",
    month: "long",
    day: "numeric",
  });
}

function getPatientName() {
  // Primary source: what the patient entered/confirmed at login —
  // this is populated for both regular and review-request flows.
  try {
    const userData = JSON.parse(localStorage.getItem("userData") || "{}");
    const full = [userData.firstName, userData.lastName]
      .filter(Boolean)
      .join(" ")
      .trim();
    if (full) return full;
  } catch {}

  // Fallback: token/selectedRow data, for flows where PHI isn't stripped
  const row = getSelectedRow();
  const td = getTokenData();
  const obj = row?.object || {};
  const full =
    obj.patientName ||
    [obj.patientFirstName, obj.patientLastName].filter(Boolean).join(" ") ||
    [td.firstName, td.lastName].filter(Boolean).join(" ") ||
    td.patientName ||
    "";
  return full.trim();
}

const RateUsStep = forwardRef(function RateUsStep(
  { onApiSuccess, onPendingChange, onVisitSelectedChange },
  ref,
) {
  // ── Visits list state ──
  const [visits, setVisits] = useState([]);
  const [visitsLoading, setVisitsLoading] = useState(true);
  const [visitsError, setVisitsError] = useState("");
  const [selectedVisit, setSelectedVisit] = useState(null);
  const [submittedVisitIds, setSubmittedVisitIds] = useState(new Set());

  // ── Feedback form state (for the selected visit) ──
  const [schema, setSchema] = useState(null);
  const [initialValues, setInitialValues] = useState(null);
  const [formLoading, setFormLoading] = useState(false);
  const [formLoadError, setFormLoadError] = useState("");
  const responsesRef = useRef({});

  const rateReviewTemplate = getRateReviewTemplate();
  const formTemplateId =
    rateReviewTemplate?.templateId ?? rateReviewTemplate?.TemplateId;

  const prefillData = useMemo(
    () => ({
      txtPatientFName: getPatientName(),
      provider_name: getTokenData().doctorName || "",
    }),
    [selectedVisit],
  );

  // async function apiFetchGooglePlaceId(baseUrl, officeId, token) {
  //   try {
  //     const url = `${baseUrl}api/CommonServiceAPI/v1/FormTemplate/GetGooglePlaceId?officeId=${officeId}`;
  //     const res = await fetch(url, {
  //       method: "GET",
  //       headers: buildHeaders(token),
  //     });
  //     const result = await res.json();
  //     console.log("[RateUsStep] GetGooglePlaceId raw response:", result); // ← temp: confirm shape
  //     if (result.responseCode === 200) {
  //       const data = result.data || result.Data || null;
  //       const placeId = data?.googlePlaceId || data?.GooglePlaceId || null;
  //       if (!placeId)
  //         console.warn(
  //           "[RateUsStep] responseCode 200 but no placeId found in:",
  //           data,
  //         );
  //       return placeId;
  //     }
  //     console.warn(
  //       "[RateUsStep] GetGooglePlaceId non-200 responseCode:",
  //       result.responseCode,
  //       result.message,
  //     );
  //   } catch (e) {
  //     console.error("[RateUsStep] apiFetchGooglePlaceId error:", e); // was already there, keep it
  //   }
  //   return null;
  // }

  function buildGoogleReviewUrl(placeId) {
    if (!placeId) return null;
    return `https://search.google.com/local/writereview?placeid=${encodeURIComponent(placeId)}`;
  }

  // const googleReviewUrl = buildGoogleReviewUrl(GOOGLE_PLACE_ID);
  const [googlePlaceId, setGooglePlaceId] = useState(null);
  const googleReviewUrl = buildGoogleReviewUrl(googlePlaceId);

  const handleFieldChange = async (fieldId, value, field) => {
    if (field?.type !== "button") return;
    if (value?.actionKey !== "google_review") return;

    // Use cached URL if we already have it
    if (googleReviewUrl) {
      window.open(googleReviewUrl, "_blank", "noopener,noreferrer");
      return;
    }

    // Otherwise fetch it now, on click
    const td = getTokenData();
    console.log("td", td);
    const row = getSelectedRow();
    const token = getCommonServiceToken();
    const baseUrl = row?.object?.commonServiceUrl;
    const companyId = td.companyId || td.CompanyId || "";
    const officeId = td.officeId || td.OfficeId || companyId;

    if (!baseUrl || !token || !officeId) {
      console.warn("[RateUsStep] cannot fetch place id — missing", {
        baseUrl,
        token: !!token,
        officeId,
      });
      toast.error("Couldn't open Google Review right now.");
      return;
    }

    // const placeId = await apiFetchGooglePlaceId(baseUrl, officeId, token);
    // if (placeId) {
    //   setGooglePlaceId(placeId); // cache for next time
    //   window.open(
    //     buildGoogleReviewUrl(placeId),
    //     "_blank",
    //     "noopener,noreferrer",
    //   );
    // } else {
    //   toast.error("Google Review link isn't available for this office yet.");
    // }
  };

  useEffect(() => {
    onVisitSelectedChange?.(!!selectedVisit);
  }, [selectedVisit]); // eslint-disable-line

  useEffect(() => {
    const td = getTokenData();
    const row = getSelectedRow();
    const patientId =
      row?.object?.patientID || td.patientId || td.PatientId || "";
    const companyId = td.companyId || td.CompanyId || "";
    const targetEventIds = getTargetEventIds();

    const token = getCommonServiceToken();
    const baseUrl = row?.object?.commonServiceUrl;
    const officeId = td.officeId || td.OfficeId || companyId;
    console.log(
      "[RateUsStep] resolved officeId for place-id lookup:",
      officeId,
      "| tokenData:",
      td,
    );
    // if (baseUrl && token && officeId) {
    //   apiFetchGooglePlaceId(baseUrl, officeId, token).then(setGooglePlaceId);
    // }
    if (!patientId || !companyId) {
      setVisitsError("Missing patient information.");
      setVisitsLoading(false);
      onPendingChange?.(false); // no visits to review — don't block Finish
      return;
    }

    (async () => {
      try {
        setVisitsLoading(true);
        setVisitsError("");
        // const res = await CallApiForGetFinalizedVisits(
        //   patientId,
        //   companyId,
        //   targetEventIds,
        // );
        // if (res?.success) {
        //   let visitList = res.data || [];
        //   if (targetEventIds.length) {
        //     const matches = visitList.filter((v) =>
        //       targetEventIds.includes(String(v.VisitId ?? v.visitId)),
        //     );
        //     visitList = matches.length
        //       ? matches
        //       : targetEventIds.map((id) => ({
        //           VisitId: id,
        //           DoctorName: td.doctorName || td.DoctorName || "",
        //         }));
        //   }

        //   setVisits(visitList);

        const res = await CallApiForGetFinalizedVisits(patientId, companyId, []);
        if (res?.success) {
          let visitList = res.data || [];

          if (targetEventIds.length) {
            const isRequested = (v) =>
              targetEventIds.includes(String(v.VisitId ?? v.visitId));
            visitList = [...visitList].sort(
              (a, b) => Number(isRequested(b)) - Number(isRequested(a)),
            );
          }

          setVisits(visitList);

          // ── Check each visit for an existing saved review ──
          if (formTemplateId && visitList.length > 0) {
            const token = getCommonServiceToken();
            const baseUrl = row?.object?.commonServiceUrl;
            if (baseUrl && token) {
              const checks = await Promise.all(
                visitList.map(async (v) => {
                  const visitId = v.VisitId ?? v.visitId;
                  const saved = await apiFetchSaved(
                    baseUrl,
                    formTemplateId,
                    visitId,
                    companyId,
                    token,
                  );
                  return { visitId, hasSaved: Object.keys(saved).length > 0 };
                }),
              );
              const doneIds = new Set(
                checks.filter((c) => c.hasSaved).map((c) => c.visitId),
              );
              setSubmittedVisitIds(doneIds);
              onPendingChange?.(doneIds.size < visitList.length); // pending if any visit lacks a review
            } else {
              onPendingChange?.(visitList.length > 0); // can't check → treat as pending if visits exist
            }
          } else {
            onPendingChange?.(false); // no template or no visits — nothing to be pending on
          }
        } else {
          setVisitsError(res?.message || "Could not load your visits.");
          onPendingChange?.(false);
        }
      } catch (e) {
        console.error("[RateUsStep] visits load error:", e);
        setVisitsError("Failed to load your visits.");
        onPendingChange?.(false);
      } finally {
        setVisitsLoading(false);
      }
    })();
  }, []); // eslint-disable-line

  // Load schema + saved answers whenever a visit is selected
  useEffect(() => {
    if (!selectedVisit) return;
    if (!formTemplateId) {
      setFormLoadError("No Rate & Review template configured.");
      return;
    }
    (async () => {
      try {
        setFormLoading(true);
        setFormLoadError("");
        const row = getSelectedRow();
        console.log("[RateUsStep] loading form for visit:", selectedVisit, "row:", row);
        const td = getTokenData();
        const companyId = td.companyId || td.CompanyId || "";
        const token = getCommonServiceToken();
        const baseUrl = row?.object?.commonServiceUrl;
        const visitId = selectedVisit.VisitId ?? selectedVisit.visitId;

        if (!baseUrl) {
          setFormLoadError("Missing service URL.");
          return;
        }
        if (!token) {
          setFormLoadError("Session expired.");
          return;
        }

        const [schemaData, savedValues] = await Promise.all([
          apiFetchSchema(baseUrl, formTemplateId, companyId, token),
          apiFetchSaved(baseUrl, formTemplateId, visitId, companyId, token),
        ]);

        if (!schemaData) {
          setFormLoadError("Could not load feedback form.");
          return;
        }

        setSchema(schemaData);
        setInitialValues(savedValues);
        responsesRef.current = savedValues;

        if (getTargetEventIds().length === 1) {
          const alreadySaved = Object.keys(savedValues).length > 0;
          onPendingChange?.(!alreadySaved);
        }
      } catch (e) {
        console.error("[RateUsStep] form load error:", e);
        setFormLoadError("Failed to load form.");
      } finally {
        setFormLoading(false);
      }
    })();
  }, [selectedVisit]); // eslint-disable-line

  const doSave = async () => {
    if (!selectedVisit || !formTemplateId || !schema) return true; // nothing open to save
    try {
      const row = getSelectedRow();
      const td = getTokenData();
      const companyId = td.companyId || td.CompanyId || "";
      const userId = td.userId || td.UserId || td.ID || "";
      const token = getCommonServiceToken();
      const baseUrl = row?.object?.commonServiceUrl;
      const formName = schema?.formTemplateName || schema?.name || "";
      const visitId = selectedVisit.VisitId ?? selectedVisit.visitId;
      const formDataObj = buildFormDataObj(schema, responsesRef.current);

      const ok = await apiSave(
        baseUrl,
        token,
        formTemplateId,
        visitId,
        companyId,
        userId,
        formName,
        formDataObj,
      );
      if (ok) {
        setSubmittedVisitIds((prev) => {
          const next = new Set(prev);
          next.add(visitId);
          onPendingChange?.(next.size < visits.length);
          return next;
        });
        toast.success("Feedback submitted successfully!");
        return true;
      }
      toast.error("Failed to submit feedback.");
      return false;
    } catch (e) {
      console.error("[RateUsStep] save error:", e);
      toast.error("Failed to submit feedback.");
      return false;
    }
  };

  useImperativeHandle(ref, () => ({
    submit: async () => {
      if (!selectedVisit) {
        onApiSuccess?.();
        return;
      } // just browsing the list — proceed
      const ok = await doSave();
      if (ok) onApiSuccess?.(); // matches other steps: save, then advance/finish
    },
    isPending: () => false,
  }));

  const handleBackToList = () => {
    setSelectedVisit(null);
    setSchema(null);
    setInitialValues(null);
    setFormLoadError("");
    responsesRef.current = {};
  };

  // ───────── Visits list view ─────────
  if (!selectedVisit) {
    return (
      <div style={{ padding: "4px 6px" }}>
        {!visitsLoading && !visitsError && visits.length > 0 && (
          <h6
            style={{
              margin: "0 0 16px",
              fontSize: 15,
              fontWeight: 700,
              color: "#1a1410",
              
            }}
          >
            Select a visit to rate
          </h6>
        )}
        {visitsLoading ? (
          <div style={{ textAlign: "center", padding: 40, color: "#7d6e61" }}>
            ⏳ Loading your visits…
          </div>
        ) : visitsError ? (
          <div style={{ textAlign: "center", padding: 40, color: "#dc3545" }}>
            {visitsError}
          </div>
        ) : visits.length === 0 ? (
          <div style={{ textAlign: "center", padding: 40, color: "#7d6e61" }}>
            No finalized visits found yet. Once a visit is completed, it'll
            appear here for feedback.
          </div>
        ) : (
          // <div
          //   style={{
          //     display: "grid",
          //     gap: 12,
          //     maxWidth: 640,
          //     margin: "0 auto",
          //   }}
          // >
          <div style={{ display: "grid", gap: 8 }}>
            {visits.map((v) => {
              const visitId = v.VisitId ?? v.visitId;
              const visitDate = v.VisitDate ?? v.visitDate;
              const doctorName = v.DoctorName ?? v.doctorName;
              const locationName = v.LocationName ?? v.locationName;
              const specialtyName = v.SpecialtyName ?? v.specialtyName;
              const visitType = v.VisitType ?? v.visitType;
              const isReviewed = submittedVisitIds.has(visitId);

              const detailLine = [
                doctorName ? `Dr. ${doctorName}` : null,
                locationName,
                specialtyName,
                visitType,
              ]
                .filter(Boolean)
                .join(" · ");

              return (
                <div
                  key={visitId}
                  style={{ display: "flex", alignItems: "center", gap: 8 }}
                >
                  <button
                    onClick={() => setSelectedVisit(v)}
                    style={{
                      flex: 1,
                      display: "flex",
                      justifyContent: "space-between",
                      alignItems: "center",
                      padding: "14px 18px",
                      borderRadius: 10,
                      border: "1px solid #e5ddd3",
                      background: "#fff",
                      cursor: "pointer",
                      textAlign: "left",
                    }}
                  >
                    <div>
                      <div
                        style={{
                          fontWeight: 600,
                          color: "#1a1410",
                          fontSize: 14,
                        }}
                      >
                        {formatVisitDate(visitDate)}
                      </div>
                      <div
                        style={{
                          fontSize: 12.5,
                          color: "#7d6e61",
                          marginTop: 2,
                        }}
                      >
                        {detailLine || "Visit details"}
                      </div>
                    </div>

                    {isReviewed ? (
                      <span
                        style={{
                          color: "#2a9d8f",
                          fontSize: 12.5,
                          fontWeight: 600,
                          whiteSpace: "nowrap",
                        }}
                      >
                        ✓ Rate & Review
                      </span>
                    ) : (
                      <span
                        style={{
                          color: "#b0a090",
                          fontSize: 12.5,
                          fontWeight: 600,
                          whiteSpace: "nowrap",
                        }}
                      >
                        ✓ Rate & Review →
                      </span>
                    )}
                  </button>
                </div>
              );
            })}
          </div>
        )}
      </div>
    );
  }

  // ───────── Feedback form view ─────────
  return (
    <div style={{ padding: "16px 20px" }}>
      {/* {!getTargetEventId() && ( */}
      <button
        onClick={handleBackToList}
        style={{
          marginBottom: 16,
          background: "none",
          border: "none",
          color: "#2a9d8f",
          cursor: "pointer",
          fontSize: 13,
          fontWeight: 600,
          padding: 0,
        }}
      >
        ← Back to visits
      </button>
      {/* )} */}

      {formLoading ? (
        <div style={{ textAlign: "center", padding: 40, color: "#7d6e61" }}>
          ⏳ Loading…
        </div>
      ) : formLoadError ? (
        <div style={{ textAlign: "center", padding: 40, color: "#dc3545" }}>
          {formLoadError}
        </div>
      ) : (
        <FormRenderer
          schema={schema}
          initialValues={initialValues}
          hideTopbar
          hideSaveButtons
          hidePreviewToggle
          googleReviewUrl={googleReviewUrl}
          onFieldChange={handleFieldChange}
          onChange={(responses) => {
            responsesRef.current = responses;
          }}
          prefillData={prefillData}
        />
      )}
    </div>
  );
});

export default RateUsStep;

export function getReviewEventIds() {
  try {
    const td = JSON.parse(sessionStorage.getItem("tokenData") || "{}");

    let extra = td.extraInfo ?? td.ExtraInfo;
    if (typeof extra === "string" && extra.trim()) {
      try { extra = JSON.parse(extra); } catch { extra = null; }
    }
    let raw = extra?.EventIds ?? extra?.eventIds;

    if (raw == null) raw = td.eventIds ?? td.EventIds;

    if (Array.isArray(raw)) return raw.map(String).filter(Boolean);
    if (typeof raw === "string" && raw.trim())
      return raw.split(",").map((s) => s.trim()).filter(Boolean);
    if (typeof raw === "number") return [String(raw)];
  } catch {}
  return [];
}